import React, { useMemo } from "react";
import { useMLSCompliance } from "../../../hooks/mls/useMLSCompliance";
import { Property } from "../../../types/search";
import { formatTimestamp } from "../../../utils/formatText";
import equalHousing from "../../../assets/equal-housing.svg";
import { UseQueryResult } from "@tanstack/react-query";

interface MLSComplianceResponse {
  status: string;
  data: {
    mlsBoardCode: string;
    mlsLogo: string;
    complianceText: string;
  };
}

interface MLSComplianceTextProps {
  mlsBoardCodes: string[];
  timestamp?: string;
  mlsComplianceQueries?: UseQueryResult<MLSComplianceResponse, Error>[];
}

export const MLSComplianceText: React.FC<MLSComplianceTextProps> = ({
  mlsBoardCodes,
  timestamp,
  mlsComplianceQueries: passedQueries,
}) => {
  // Get unique MLS board codes
  const visibleMLSCodes = useMemo(() => {
    const codes = new Set<string>();
    mlsBoardCodes.forEach((code) => {
      if (code) {
        codes.add(code);
      }
    });
    return Array.from(codes);
  }, [mlsBoardCodes]);

  // Fetch compliance data for all visible MLS boards (only if not passed)
  const fetchedQueries = useMLSCompliance(passedQueries ? [] : visibleMLSCodes);

  // Use passed queries if available, otherwise use fetched ones
  const mlsComplianceQueries = passedQueries || fetchedQueries;

  // Check if any queries are loading
  const isLoading = mlsComplianceQueries.some((query) => query.isLoading);

  // Filter out any failed queries and extract compliance data
  const complianceData = mlsComplianceQueries
    .filter((query) => query.isSuccess && query.data?.status === "success")
    .map((query) => query.data!.data);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  if (!complianceData.length) {
    return null;
  }

  return (
    <div className="space-y-6">
      {complianceData.map((mls) => (
        <div
          key={mls.mlsBoardCode}
          className="text-[10px] lg:text-[13px] text-gray-500"
        >
          <div>
            {mls.mlsLogo && (
              <img
                src={mls.mlsLogo}
                alt={`${mls.mlsBoardCode} logo`}
                className="h-10 mb-2 block"
                loading="lazy"
                decoding="async"
              />
            )}

            {/* Add NWMLS specific text */}
            {mls.mlsBoardCode.toLowerCase() === "nwmls" && (
              <p className="text-[13px] md:text-base font-normal mb-3">
                Listings marked with the three tree icon are provided courtesy
                of NWMLS.
              </p>
            )}
            <div className="mt-2">
              {mls.complianceText && (
                <>
                  {(() => {
                    const afterReplace = mls.complianceText.replace(
                      /listing updated: \d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2} [AP]M/i,
                      ""
                    );

                    return afterReplace
                      .split("\n")
                      .map((paragraph, index) => (
                        <p
                          key={`${mls.mlsBoardCode}-${index}`}
                          className="mb-1"
                          dangerouslySetInnerHTML={{ __html: paragraph }}
                        />
                      ));
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      ))}
      <div className="space-y-2">
        {timestamp && (
          <p className="text-[10px] lg:text-[13px] text-gray-500">
            Listing Updated: {formatTimestamp(timestamp)}
          </p>
        )}
        <div className="flex items-center gap-2">
          <img
            src={equalHousing}
            alt="Equal Housing Opportunity"
            className="w-8 h-10"
            loading="lazy"
            decoding="async"
          />
          <p className="text-[10px] lg:text-[13px] text-gray-500">
            This site is protected by the{" "}
            <a href="/dmca" className="text-primary underline">
              DMCA
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};
